import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {InvoicingPeriod, invoicingPeriodQuery, invoicingPeriodService} from '@store/settings/invoicingPeriod';
import {useObservable} from '@ngneat/react-rxjs';
import {LinearProgress, Stack, Typography} from '@mui/material';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {usersQuery} from '@store/settings/users';
import NewTableComponent from '@components/table/NewTable.component';
import dayjs from 'dayjs';
import {LoadingButton} from '@mui/lab';
import ConfirmModal from '@components/modal/Confirm.modal';
import ModalComponent from '@components/modal/Modal.component';
import {fromEvent} from 'rxjs';
import * as io from 'socket.io-client';
import env from '@config/env.config';

const InvoicingPeriodTab = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [{invoicingPeriods, loading}] = useObservable(invoicingPeriodQuery.invoicingPeriods$);

  const [closeLoading, setCloseLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [provisionsLoading, setProvisionsLoading] = useState(false);

  const [progress, setProgress] = useState<number>(0);
  const [progressStepTitle, setProgressStepTitle] = useState<string>(t('invoicingPeriod.download.progressStep1'));

  const [downloadingInvoices, setDownloadingInvoices] = useState(false);

  useEffect(() => {
    invoicingPeriodService.getInvoicingPeriods().subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  const downloadInvoicingPeriodInvoicesCsv = (invoicingPeriodId: number) => {
    setDownloadingInvoices(true);
    setProgress(0);
    const token = Math.random().toString(16).slice(2);
    const ioClient = io.connect(`${env.API_URL}${env.API_URL.endsWith('/') ? '' : '/'}ws`, {
      path: '/api/ws/socket.io'
    });
    invoicingPeriodService.downloadInvoicingPeriodInvoicesCsv(invoicingPeriodId, token).subscribe({
      next: () => {
        setDownloadingInvoices(false);
        setProgress(0);
        enqueueSnackbar(t('invoicingPeriod.download.success'), {variant: 'success'});
      },
      error: err => enqueueSnackbar(err.text, err.options),
    });
    ioClient.on('connect', () => {
      fromEvent(ioClient, `progress-${token}`).subscribe((data: any[]) => {
        const progressPct = data[0];
        const title = data[1];
        setProgressStepTitle(t(`invoicingPeriod.download.${title}`));
        setProgress(progressPct);
      });
    });
  };

  const handleSetNextPeriod = () => {
    setCloseLoading(true);
    setShowConfirmClose(false);
    invoicingPeriodService.setNextPeriod().subscribe({
      next: () => {
        setCloseLoading(false);
        enqueueSnackbar(t('invoicingPeriod.close.success'), {variant: 'success'});
      },
      error: err => {
        setCloseLoading(false);
        enqueueSnackbar(err.text, err.options);
      }
    });
  };

  const handleSupplierInvoicesSage = () => {
    setSuppliersLoading(true);
    invoicingPeriodService.interfaceSupplierInvoicesSage().subscribe({
      next: () => {
        setSuppliersLoading(false);
        enqueueSnackbar(t('invoicingPeriod.close.successSupplierSage'), {variant: 'success'});
      },
      error: err => {
        setSuppliersLoading(false);
        enqueueSnackbar(err.text, err.options);
      }
    });
  };

  const handleClientInvoicesSage = () => {
    setClientsLoading(true);
    invoicingPeriodService.interfaceClientInvoicesSage().subscribe({
      next: () => {
        setClientsLoading(false);
        enqueueSnackbar(t('invoicingPeriod.close.successClientsSage'), {variant: 'success'});
      },
      error: err => {
        setClientsLoading(false);
        enqueueSnackbar(err.text, err.options);
      }
    });
  };

  const handleProvisionInvoicesSage = () => {
    setProvisionsLoading(true);
    invoicingPeriodService.interfaceProvisionsSage().subscribe({
      next: () => {
        setProvisionsLoading(false);
        enqueueSnackbar(t('invoicingPeriod.close.successProvisionsSage'), {variant: 'success'});
      },
      error: err => {
        setProvisionsLoading(false);
        enqueueSnackbar(err.text, err.options);
      }
    });
  };

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize="30px" fontWeight="700">
            {t('routes.accounting')}
          </Typography>
          <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
            <LoadingButton loading={suppliersLoading} onClick={handleSupplierInvoicesSage} variant="contained"
                           color="primary">
              {t('invoicingPeriod.supplierInvoicesSage')}
            </LoadingButton>
            <LoadingButton loading={clientsLoading} onClick={handleClientInvoicesSage} variant="contained"
                           color="primary">
              {t('invoicingPeriod.clientInvoicesSage')}
            </LoadingButton>
            <LoadingButton loading={provisionsLoading} onClick={handleProvisionInvoicesSage} variant="contained"
                           color="primary">
              {t('invoicingPeriod.provisionsSage')}
            </LoadingButton>
            <LoadingButton loading={closeLoading} onClick={() => setShowConfirmClose(true)} variant="contained"
                           color="primary">
              {t('invoicingPeriod.closeButton')}
            </LoadingButton>
          </Stack>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'periodEnd',
              label: t('settings.invoicingPeriod.table.periodEnd'),
              valueGetter: (params: GridValueGetterParams<any, InvoicingPeriod>) => dayjs(params.row.periodEnd).format('MM.YYYY'),
              fullWidth: true,
              minWidth: 350
            }),
            COLUMN_TYPES.TEXT({
              field: 'closedBy',
              label: t('settings.invoicingPeriod.table.closedBy'),
              valueGetter: (params: GridValueGetterParams<InvoicingPeriod, InvoicingPeriod>) => params.row.closedBy ? [params.row.closedBy?.firstName, params.row.closedBy?.lastName].join(' ') : '-',
              fullWidth: true,
              minWidth: 300
            }),
            COLUMN_TYPES.TEXT({
              field: 'closedAt',
              label: t('settings.invoicingPeriod.table.closedAt'),
              valueGetter: (params: GridValueGetterParams<InvoicingPeriod, InvoicingPeriod>) => params.row.closedAt ? dayjs(params.row.closedAt).format('DD.MM.YYYY') : '-',
              fullWidth: true,
              minWidth: 300
            }),
            COLUMN_TYPES.ACTIONS({
              handleDownload: (invoicingPeriod: any) => {
                downloadInvoicingPeriodInvoicesCsv(invoicingPeriod.row.id);
              },
            }),
          ]}
          disableRowSelection
          disablePagination
          totalRows={usersQuery.totalRows}
          data={invoicingPeriods}/>
      </Stack>
      {showConfirmClose &&
          <ConfirmModal handleClose={() => setShowConfirmClose(false)} handleValidate={handleSetNextPeriod}
                        validateButton={t('settings.invoicingPeriod.confirmModal.button')}
                        description={t('settings.invoicingPeriod.confirmModal.description')}
                        title={t('settings.invoicingPeriod.confirmModal.title')}/>}
      {downloadingInvoices && <ModalComponent handleClose={() => setDownloadingInvoices(false)}
                                              title={'Export de la période de facturation'} content={
        <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={3} alignItems="center">
          <Typography>{progressStepTitle}</Typography>
          <LinearProgress sx={{width: '100%'}} variant="determinate"
                          value={progress}/>
          <Typography>{progress}%</Typography>
        </Stack>
      }/>}
    </>
  );
};

export default InvoicingPeriodTab;
